import React, { ChangeEvent, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Alert, Box, Button, InputAdornment, Pagination, Paper, Stack, TextField, useTheme } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";

import TopBar from "components/dashboardParts/TopBar";
import APIRequest, { APIError } from "system/APIRequest";
import TagManagementRow from "./TagManagementRow";
import TagDialog from "./TagDialog";
import DeleteConfirmation from "./DeleteConfirmation";
import { TextLoader } from "components/apiHandling/TextLoader";
import { useDebounce } from "hooks/debounce";
import SortButton from "./SortButton";
import TagRequestHelper from "./TagRequestHelper";

export interface Tag {
  color?: string;
  created_at?: string;
  dashboard_count?: number;
  description?: string;
  id: string;
  name: string;
  query_count?: number;
  updated_at?: string;
  use_count?: number;
}

export interface ApiPagination {
  cursor?: string;
  page: number;
  per_page: number;
  total: number;
}

type TagSortValue = "dashboard_count" | "name" | "query_count" | "updated_at" | "use_count";

interface TagManagementPageProps {

}

export default function TagManagementPage(props: TagManagementPageProps): React.JSX.Element {
  const { i18n } = useTranslation();
  const theme = useTheme();

  const sortByOptions: Array<{label: string, value: TagSortValue}> = [{
    label: i18n.t("tag-management-page.sort-options.field-last-updated"),
    value: "updated_at"
  }, {
    label: i18n.t("tag-management-page.sort-options.field-name"),
    value: "name"
  }, {
    label: i18n.t("tag-management-page.sort-options.field-usage"),
    value: "use_count"
  }];
  const initialSortBy = sortByOptions.find((option) => option.value === "use_count") || sortByOptions[0];

  const [search, setSearch] = useState<string>("");
  const [tags, setTags] = useState<Array<Tag>>([]);
  const [sortBy, setSortBy] = useState<string>(initialSortBy.value);
  const [sortOrder, setSortOrder] = useState<string>("desc");
  const [selectedTag, setSelectedTag] = useState<Tag | null>(null);
  const [isLoadingTags, setIsLoadingTags] = useState<boolean>(false);
  const [showTagDialog, setShowDialog] = useState<boolean>(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState<boolean>(false);
  const pagination = useRef<ApiPagination>({
    page: 1,
    per_page: 20,
    total: 0
  });

  const pageCount = Math.ceil(pagination.current.total / pagination.current.per_page);
  const showPagination = pagination.current.total > pagination.current.per_page;

  const loadTags = useDebounce(async () => {
    try {
      setIsLoadingTags(true);
      const queryParams = {
        page: pagination.current.page+"",
        per_page: pagination.current.per_page+"",
        search: search,
        sort: sortBy,
        sort_order: sortOrder
      };
      const response = await APIRequest("obj/resource-tag", queryParams);
      setTags(response.output);
      pagination.current = response.pagination;
    } catch (e) {
      const error = e as APIError;
      // TODO
    } finally {
      setIsLoadingTags(false);
    }
  }, 500);

  async function deleteTag(tag: Tag) {
    if (!tag.id) {
      return;
    }
    await TagRequestHelper.delete(tag.id);
    loadTags();
  }

  useEffect(() => {
    loadTags();
  }, [search, sortBy, sortOrder]);

  const tagRows = tags.map((tag, index) => {
    return (
      <TagManagementRow
        key={`TagManagementRow_${index}`}
        onClick={() => {
          setSelectedTag(tag);
          setShowDialog(true);
        }}
        onDelete={() => {
          setSelectedTag(tag);
          setShowDeleteConfirmation(true);
        }}
        tag={tag}
      />
    )
  });

  return (
    <Box sx={{ maxWidth: "1300px", margin: "0 auto", pl: 2, pr: 2, position: "relative" }}>
      <TopBar showLogo={true} title={i18n.t("tag-management-page.title")} />
      <Stack direction="row" sx={{
        justifyContent: "center"
      }}>
        <Button color="success" onClick={() => setShowDialog(true)} variant="contained">Create Tag</Button>
      </Stack>
      <Stack
        sx={{
          borderRadius: theme.general.borderRadius,
          marginTop: 1
        }}>
        <Paper sx={{ marginBottom: 1 }}>
          <Stack
            direction="row"
            sx={{
              gap: 1,
              padding: 1
            }}>
            <TextField
              fullWidth
              autoComplete="off"
              onChange={(event: ChangeEvent<HTMLInputElement>) => setSearch(event.target.value)}
              placeholder={i18n.t("tag-management-page.field-search-placeholder")}
              size="small"
              slotProps={{
                input: {
                  startAdornment: <InputAdornment position="start"><SearchIcon/></InputAdornment>
                }
              }}
              value={search}
            />
            <SortButton 
              initialOrder="desc"
              initialSort={initialSortBy.value}
              onChange={(sortBy, order) => {
                setSortBy(sortBy);
                setSortOrder(order);
              }}
              sortByOptions={sortByOptions} />
          </Stack>
        </Paper>
        {isLoadingTags &&
          <Box
            sx={{
              alignSelf: "center",
              marginTop: 2,
              marginBottom: 2
            }}>
            <TextLoader />
          </Box>
        }
        {!isLoadingTags &&
          <Stack sx={{
            gap: 0.5
          }}>
            {tagRows}
          </Stack>
        }
        {!isLoadingTags && tags.length === 0 && 
          ((search && 
            <Paper>
              <Stack sx={{
                padding: 1
              }}>
                <Alert severity="warning">{i18n.t("tag-management-page.no-matches")}</Alert>
              </Stack>
            </Paper>
          )
          || 
          (!search &&
            <Paper>
              <Stack sx={{
                padding: 1
              }}>
                <Alert severity="info">{i18n.t("tag-management-page.no-tags")}</Alert>
              </Stack>
            </Paper>
          ))
        }
        {showPagination &&
          <Stack
            sx={{
              alignItems: "center",
              padding: 1
            }}>
            <Pagination
              count={pageCount}
              onChange={(event, page) => {
                pagination.current.page = page;
                loadTags();
              }}
              page={pagination.current.page}
            />
          </Stack>
        }
      </Stack>
      {showTagDialog &&
        <TagDialog
          onClose={(saved) => {
            setShowDialog(false);
            setSelectedTag(null);
            if (!!saved) {
              loadTags();
            }
          }}
          tag={selectedTag}
        />
      }
      {showDeleteConfirmation &&
        <DeleteConfirmation 
          onClose={(result) => {
            if (result && selectedTag) {
              deleteTag(selectedTag);
              loadTags();
            }
            setSelectedTag(null);
            setShowDeleteConfirmation(false);
          }}
        />
      }
    </Box>
  );
}